import { useState } from 'react'
import { toast } from 'sonner'

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { ChatItem } from '../../models/chat'
import {
  type ChatActionWithValue,
  type InsuranceApplication,
  useEditChatItemMutation,
} from '../../models/insuranceApplication'
import { ChatMessageComponent } from './ChatMessageComponent'

const SERVICE_TITLES: Record<string, string> = {
  ADD_TRAVEL_DISTANCE: 'Edit driving distance',
  ADD_DOB: 'Edit date of birth',
  CONFIRM_DOT_NUMBER: 'Edit DOT confirmation',
  COVERAGE_START_DATE: 'Edit start date',
}

interface EditChatItemDialogProps {
  application: InsuranceApplication
  chatItem: ChatItem | null
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function EditChatItemDialog({
  application,
  chatItem,
  onOpenChange,
  onSuccess,
}: EditChatItemDialogProps) {
  const [submitting, setSubmitting] = useState(false)
  const { mutateAsync } = useEditChatItemMutation({ application })

  if (!chatItem || !chatItem.editableAction) return null

  const service = chatItem.service || ''
  const title = SERVICE_TITLES[service] || 'Edit answer'

  async function handleEditSubmit(data: ChatActionWithValue | FormData) {
    if (data instanceof FormData || !chatItem) return
    setSubmitting(true)
    try {
      await mutateAsync({
        chatItemId: chatItem.id,
        service: data.service || service,
        content: data.content,
        value: data.value,
      })
      toast.success('Answer updated')
      onSuccess()
      onOpenChange(false)
    } catch {
      toast.error('Failed to update answer')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={!!chatItem} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="mt-2">
          <ChatMessageComponent
            content={chatItem.editableAction.content}
            handleSubmit={handleEditSubmit}
            readonly={submitting}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
